import { ChangeDetectorRef, Directive, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Base component for handling loading states with automatic change detection.
 * 
 * Usage:
 * 1. Extend this class in your component
 * 2. Use `this.setLoading(true/false)` instead of directly setting isLoading
 * 3. The change detection will be triggered automatically
 * 
 * Example:
 * ```typescript
 * export class MyComponent extends LoadingBaseComponent {
 *   constructor(cdr: ChangeDetectorRef) {
 *     super(cdr);
 *   }
 * 
 *   loadData() {
 *     this.setLoading(true);
 *     this.apiService.getData().subscribe(data => {
 *       this.data = data;
 *       this.setLoading(false); // Automatically triggers change detection
 *     });
 *   }
 * }
 * ```
 */
@Directive()
export abstract class LoadingBaseComponent implements OnDestroy {
  isLoading: boolean = false;
  protected destroy$ = new Subject<void>();

  constructor(protected cdr: ChangeDetectorRef) {}

  /**
   * Set loading state and trigger change detection
   * @param loading - true to show skeleton, false to hide
   */
  protected setLoading(loading: boolean): void {
    this.isLoading = loading;
    this.cdr.detectChanges();
  }

  /**
   * Helper method for API calls with automatic loading state management
   * @param apiCall - Function that returns the API observable
   * @param onSuccess - Callback when API succeeds
   * @param onError - Optional error callback
   */
  protected withLoading<T>(
    apiCall: () => void,
    onComplete?: () => void
  ): void {
    this.setLoading(true);
    // The apiCall should handle setting loading to false in its callback
    apiCall();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
